import {useEffect, useRef, useState} from 'react';
import styles from '../../Deadlines/DeadlinesTable/DeadlinesTable.module.css';
import Input from '../../../../components/Input/Input';
import {CustomSelect} from '../../../../components/Select/Select';
import UniversalTable from '../../../../components/UTable/UTable';
import Button from '../../../../components/Button/Button';
import IconButton from '../../../../components/IconButton/IconButton';
import ErrorMessage from '../../../../components/ErrorMessage/ErrorMessage';
import {Modal} from '../../../../components/Modals/Modal';
import {IssueSIZFromNeed} from '../../../../components/Modals/IssueSIZFromNeed/IssueSIZFromNeed';
import useFillSelect from '../../../../hooks/useFillSelect.hook';
import {
	getAllDivisionsUrl,
	getAllMaterialTypesUrl,
	getAllProfessionsUrl,
	getSizNeedByEmployeeUrl
} from '../../../../helpers/constants';
import {columns} from './table_columns';

const normalize = id => (id === undefined || id === null) ? id : String(id);

export function SIZNeedTable({presetMaterialTypeId, onIssued}) {
	const [totalRecords, setTotalRecords] = useState(0);
	const [lastNameFilter, setLastNameFilter] = useState(undefined);
	const [selectedProfOption, setSelectedProfOption] = useState(null);
	const [selectedDivOption, setSelectedDivOption] = useState(null);
	const [selectedTypeOption, setSelectedTypeOption] = useState(null);
	const [errorApi, setErrorApi] = useState(undefined);
	const [selectedRows, setSelectedRows] = useState({});
	const [isIssueOpen, setIssueOpen] = useState(false);
	const [issueRecords, setIssueRecords] = useState([]);
	const [refreshKey, setRefreshKey] = useState(0);
	const issueCreatedRef = useRef(false);
	const issueBusyRef = useRef(false);

	const {
		error: errorTypes,
		options: optionsTypes,
		itemDict: typesDict
	} = useFillSelect({
		endpoint: getAllMaterialTypesUrl,
		labelField: 'title'
	});

	useEffect(() => {
		if (optionsTypes.length === 0) {
			return;
		}
		if (presetMaterialTypeId != null) {
			const option = optionsTypes.find(
				(item) => String(item.value) === String(presetMaterialTypeId)
			);
			if (option) {
				setSelectedTypeOption(option);
			}
			return;
		}
		setSelectedTypeOption((prev) => prev ?? optionsTypes[0]);
	}, [presetMaterialTypeId, optionsTypes]);

	const {
		error: errorProf,
		options: optionsProf
	} = useFillSelect({
		endpoint: getAllProfessionsUrl,
		labelField: 'title'
	});

	const {
		error: errorDiv,
		options: optionsDiv
	} = useFillSelect({
		endpoint: getAllDivisionsUrl,
		labelField: 'title'
	});

	useEffect(() => {
		setErrorApi(errorTypes || errorProf || errorDiv);
	}, [errorTypes, errorProf, errorDiv]);

	useEffect(() => {
		setSelectedRows({});
	}, [selectedTypeOption?.value]);

	useEffect(() => {
		if (!selectedTypeOption) {
			setTotalRecords(0);
		}
	}, [selectedTypeOption]);

	const [debouncedLastName, setDebouncedLastName] = useState(lastNameFilter);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedLastName(lastNameFilter);
		}, 500);
		return () => clearTimeout(timer);
	}, [lastNameFilter]);

	let filters = {};
	if (debouncedLastName) {
		filters = {...filters, last_name: debouncedLastName};
	}
	if (selectedProfOption) {
		filters = {...filters, profession_id: selectedProfOption.value};
	}
	if (selectedDivOption) {
		filters = {...filters, division_id: selectedDivOption.value};
	}
	if (selectedTypeOption) {
		filters = {...filters, material_type_id: selectedTypeOption.value};
	}

	const ClearFilters = () => {
		setSelectedProfOption(null);
		setSelectedDivOption(null);
		setLastNameFilter('');
		setSelectedTypeOption(null);
		setSelectedRows({});
	};

	const getSelectedNormalized = () => Object.keys(selectedRows);

	const addRecords = (records) => {
		setSelectedRows(prev => {
			const next = {...prev};
			(records || []).forEach(record => {
				const id = normalize(record?.id ?? record?.key);
				if (id !== undefined && id !== null && record) {
					next[id] = record;
				}
			});
			return next;
		});
	};

	const removeIds = (ids) => {
		setSelectedRows(prev => {
			const next = {...prev};
			ids.forEach(id => {
				delete next[normalize(id)];
			});
			return next;
		});
	};

	const rowSelection = {
		selectedRowKeys: getSelectedNormalized(),
		preserveSelectedRowKeys: true,
		onSelect: (record, selected) => {
			if (selected) addRecords([record]);
			else removeIds([record.id ?? record.key]);
		},
		onSelectAll: (selected, selectedRowsOnPage, changeRows) => {
			const rows = (changeRows && changeRows.length) ? changeRows : (selectedRowsOnPage || []);
			if (selected) addRecords(rows);
			else removeIds(rows.map(r => r.id ?? r.key));
		}
	};

	const selectedCount = getSelectedNormalized().length;

	const handleIssued = () => {
		issueCreatedRef.current = false;
		issueBusyRef.current = false;
		setIssueOpen(false);
		setIssueRecords([]);
		setSelectedRows({});
		setRefreshKey((prev) => prev + 1);
		onIssued?.();
	};

	const handleIssueClose = () => {
		if (issueBusyRef.current) {
			return;
		}
		if (issueCreatedRef.current) {
			handleIssued();
			return;
		}
		setIssueOpen(false);
	};

	const openIssueModal = () => {
		issueCreatedRef.current = false;
		issueBusyRef.current = false;
		setIssueRecords(Object.values(selectedRows));
		setIssueOpen(true);
	};

	return (
		<div className={styles.outer_table}>
			<h2 className={styles.section_title}>Список потребности в СИЗ</h2>
			<ErrorMessage
				error={errorApi}
				onClose={() => setErrorApi(undefined)}
			/>
			<div className={styles.table_toolbar}>
				<div className={styles.filters_table}>
					<CustomSelect
						value={selectedTypeOption}
						options={optionsTypes}
						placeholder="Тип материала"
						onChange={setSelectedTypeOption}
					/>
					<Input
						value={lastNameFilter}
						onChange={(e) => setLastNameFilter(e.target.value)}
						placeholder="Поиск по фамилии"
					/>
					<CustomSelect
						value={selectedProfOption}
						options={optionsProf}
						placeholder="Профессия"
						onChange={setSelectedProfOption}
					/>
					<CustomSelect
						value={selectedDivOption}
						options={optionsDiv}
						placeholder="Подразделение"
						onChange={setSelectedDivOption}
					/>
					<IconButton onClick={ClearFilters} title="очистить фильтр">
						<img src="/icons/drop-filters-icon.svg" alt="очистить фильтр"/>
					</IconButton>
				</div>
				<div className={styles.table_actions}>
					{selectedCount > 0 && (
						<span className={styles.selected_count}>Выбрано: {selectedCount}</span>
					)}
					<div className={styles.table_count}>
						Записей: {totalRecords}
					</div>
					<Button
						disabled={selectedCount === 0 || !selectedTypeOption}
						onClick={openIssueModal}
					>
						Выдать
					</Button>
				</div>
			</div>
			<div className={styles.table}>
				{selectedTypeOption && (
					<UniversalTable
						endpoint={getSizNeedByEmployeeUrl}
						columns={columns}
						usePagination={false}
						initialPage={1}
						initialPageSize={15}
						filters={filters}
						refreshKey={refreshKey}
						onTotalRecordsChange={setTotalRecords}
						tableProps={{rowSelection}}
					/>
				)}
			</div>
			<Modal isOpen={isIssueOpen} onClose={handleIssueClose}>
				<IssueSIZFromNeed
					selectedRecords={issueRecords}
					materialTypeId={selectedTypeOption?.value}
					materialTypeTitle={selectedTypeOption?.label}
					typesDict={typesDict}
					onSuccess={handleIssued}
					onBusyChange={(busy) => { issueBusyRef.current = busy; }}
					onCreated={() => { issueCreatedRef.current = true; }}
				/>
			</Modal>
		</div>
	);
}
